import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Plus, 
  Trash2, 
  Tag, 
  Copy,
  Check,
  Calendar,
  Percent,
  DollarSign,
  Edit
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`);
      setCoupons(response.data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      toast.success("Coupon deleted");
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/admin/coupons/${coupon.id}`, {
        is_active: !coupon.is_active
      });
      toast.success(coupon.is_active ? "Coupon deactivated" : "Coupon activated");
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to update coupon");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Coupon code copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-coupons">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
              Coupon Codes
            </h1>
            <p className="text-[#A1A1AA]">Create and manage discount codes</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="create-coupon-btn"
          >
            <Plus className="w-5 h-5" />
            Create Coupon
          </button>
        </div>

        {/* Coupons Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] border border-[rgba(212,175,55,0.15)]">
            <Tag className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl font-['Playfair_Display'] text-[#F5F5F0] mb-2">No Coupons Yet</h3>
            <p className="text-[#A1A1AA] mb-6">Create your first discount coupon</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Coupon
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coupons.map((coupon) => (
              <motion.div
                key={coupon.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-[#121212] border p-6 ${
                  coupon.is_active 
                    ? 'border-[rgba(212,175,55,0.3)]' 
                    : 'border-[rgba(212,175,55,0.1)] opacity-60'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded ${
                      coupon.discount_type === 'percentage' 
                        ? 'bg-[rgba(212,175,55,0.1)]' 
                        : 'bg-[rgba(138,126,87,0.1)]'
                    }`}>
                      {coupon.discount_type === 'percentage' 
                        ? <Percent className="w-5 h-5 text-[#D4AF37]" />
                        : <DollarSign className="w-5 h-5 text-[#8A7E57]" />
                      }
                    </div>
                    <div>
                      <button
                        onClick={() => copyCode(coupon.code)}
                        className="flex items-center gap-2 text-[#F5F5F0] font-mono text-lg font-bold hover:text-[#D4AF37] transition-colors"
                      >
                        {coupon.code}
                        {copiedCode === coupon.code 
                          ? <Check className="w-4 h-4 text-green-500" />
                          : <Copy className="w-4 h-4" />
                        }
                      </button>
                      <p className="text-[#D4AF37]">
                        {coupon.discount_type === 'percentage' 
                          ? `${coupon.discount_value}% off`
                          : `C$${coupon.discount_value} off`
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingCoupon(coupon)}
                      className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                      title="Edit coupon"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCoupon(coupon.id)}
                      className="p-2 text-[#A1A1AA] hover:text-red-500 transition-colors"
                      title="Delete coupon"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-[#A1A1AA] mb-4">
                  <div className="flex justify-between">
                    <span>Applies to:</span>
                    <span className="text-[#F5F5F0] capitalize">
                      {coupon.applicable_to === 'specific' ? 'Specific Products' : coupon.applicable_to}
                    </span>
                  </div>
                  {/* Show specific products if applicable */}
                  {coupon.event_ids && coupon.event_ids.length > 0 && (
                    <div className="text-xs text-[#a1a1aa]">
                      Events: {coupon.event_ids.length} selected
                    </div>
                  )}
                  {coupon.package_ids && coupon.package_ids.length > 0 && (
                    <div className="text-xs text-[#a1a1aa]">
                      Packages: {coupon.package_ids.length} selected
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Uses:</span>
                    <span className="text-[#F5F5F0]">
                      {coupon.uses_count} / {coupon.max_uses || '∞'}
                    </span>
                  </div>
                  {coupon.valid_until && (
                    <div className="flex justify-between">
                      <span>Expires:</span>
                      <span className="text-[#F5F5F0]">{coupon.valid_until}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleToggleActive(coupon)}
                  className={`w-full py-2 text-sm uppercase tracking-wider transition-colors ${
                    coupon.is_active
                      ? 'bg-green-900/20 text-green-500 hover:bg-green-900/30'
                      : 'bg-[rgba(212,175,55,0.1)] text-[#A1A1AA] hover:text-[#D4AF37]'
                  }`}
                >
                  {coupon.is_active ? 'Active' : 'Inactive'}
                </button>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create Coupon Modal */}
        {showCreateModal && (
          <CouponModal
            onClose={() => setShowCreateModal(false)}
            onSave={() => {
              setShowCreateModal(false);
              fetchCoupons();
            }}
          />
        )}

        {/* Edit Coupon Modal */}
        {editingCoupon && (
          <CouponModal
            editCoupon={editingCoupon}
            onClose={() => setEditingCoupon(null)}
            onSave={() => {
              setEditingCoupon(null);
              fetchCoupons();
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Coupon Modal Component
const CouponModal = ({ onClose, onSave, editCoupon = null }) => {
  const isEditing = !!editCoupon;
  const [formData, setFormData] = useState({
    code: editCoupon?.code || "",
    discount_type: editCoupon?.discount_type || "percentage",
    discount_value: editCoupon?.discount_value || 10,
    max_uses: editCoupon?.max_uses || "",
    valid_from: editCoupon?.valid_from || "",
    valid_until: editCoupon?.valid_until || "",
    applicable_to: editCoupon?.applicable_to || "all",
    event_ids: editCoupon?.event_ids || [],
    package_ids: editCoupon?.package_ids || [],
    ticket_types: editCoupon?.ticket_types || [],
    landing_page_ids: editCoupon?.landing_page_ids || [],
    is_active: editCoupon?.is_active !== undefined ? editCoupon.is_active : true
  });
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Fetch events, packages, and landing pages for selection
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [eventsRes, packagesRes, landingPagesRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/packages`),
          axios.get(`${API}/admin/event-landing-pages`)
        ]);
        setEvents(eventsRes.data);
        setPackages(packagesRes.data.filter(p => p.price > 0));
        setLandingPages(landingPagesRes.data || []);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const toggleEventSelection = (eventId) => {
    setFormData(prev => ({
      ...prev,
      event_ids: prev.event_ids.includes(eventId)
        ? prev.event_ids.filter(id => id !== eventId)
        : [...prev.event_ids, eventId],
      ticket_types: [] // Reset ticket types when events change
    }));
  };

  const togglePackageSelection = (packageId) => {
    setFormData(prev => ({
      ...prev,
      package_ids: prev.package_ids.includes(packageId)
        ? prev.package_ids.filter(id => id !== packageId)
        : [...prev.package_ids, packageId]
    }));
  };

  const toggleTicketType = (ticketName) => {
    setFormData(prev => ({
      ...prev,
      ticket_types: prev.ticket_types.includes(ticketName)
        ? prev.ticket_types.filter(t => t !== ticketName)
        : [...prev.ticket_types, ticketName]
    }));
  };

  // Get all ticket types from selected events
  const getAvailableTicketTypes = () => {
    const ticketTypes = new Set();
    formData.event_ids.forEach(eventId => {
      const event = events.find(e => e.id === eventId);
      if (event?.ticket_options) {
        event.ticket_options.forEach(ticket => {
          ticketTypes.add(ticket.name);
        });
      }
    });
    return Array.from(ticketTypes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code) {
      toast.error("Please enter a coupon code");
      return;
    }

    if (formData.applicable_to === "specific") {
      if (formData.event_ids.length === 0 && formData.package_ids.length === 0 && formData.landing_page_ids.length === 0) {
        toast.error("Please select at least one event, experience, or landing page");
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        event_ids: formData.event_ids.length > 0 ? formData.event_ids : null,
        package_ids: formData.package_ids.length > 0 ? formData.package_ids : null,
        ticket_types: formData.ticket_types.length > 0 ? formData.ticket_types : null,
        landing_page_ids: formData.landing_page_ids.length > 0 ? formData.landing_page_ids : null
      };

      if (isEditing) {
        await axios.put(`${API}/admin/coupons/${editCoupon.id}`, payload);
        toast.success("Coupon updated");
      } else {
        await axios.post(`${API}/admin/coupons`, payload);
        toast.success("Coupon created");
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} coupon`);
    } finally {
      setSaving(false);
    }
  };

  const toggleLandingPageSelection = (pageId) => {
    setFormData(prev => ({
      ...prev,
      landing_page_ids: prev.landing_page_ids.includes(pageId)
        ? prev.landing_page_ids.filter(id => id !== pageId)
        : [...prev.landing_page_ids, pageId]
    }));
  };

  const showProductSelection = ["events", "experiences", "specific"].includes(formData.applicable_to);
  const showLandingPageSelection = formData.applicable_to === "specific";
  const availableTicketTypes = getAvailableTicketTypes();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] w-full max-w-2xl my-8"
      >
        <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
            {isEditing ? 'Edit Coupon' : 'Create Coupon'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="form-label">Coupon Code *</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 font-mono uppercase focus:border-[#D4AF37] focus:outline-none"
                placeholder="e.g., SUMMER20"
                required
              />
              <button
                type="button"
                onClick={generateCode}
                className="btn-secondary px-4"
              >
                Generate
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="form-label">Discount Type</label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (C$)</option>
              </select>
            </div>
            <div>
              <label className="form-label">Discount Value *</label>
              <input
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                min="0"
                max={formData.discount_type === 'percentage' ? 100 : 10000}
                required
              />
            </div>
          </div>

          <div>
            <label className="form-label">Applies To</label>
            <select
              value={formData.applicable_to}
              onChange={(e) => setFormData({ 
                ...formData, 
                applicable_to: e.target.value,
                event_ids: [],
                package_ids: [],
                landing_page_ids: []
              })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="all">All Bookings (Events + Experiences + Landing Pages)</option>
              <option value="events">Events Only</option>
              <option value="experiences">Experiences Only</option>
              <option value="landing_pages">All Landing Pages (Current & Future)</option>
              <option value="specific">Specific Products</option>
            </select>
          </div>

          {/* Product Selection */}
          {showProductSelection && !loadingProducts && (
            <div className="space-y-4">
              {/* Events Selection */}
              {(formData.applicable_to === "events" || formData.applicable_to === "specific") && (
                <div className="border border-[rgba(212,175,55,0.15)] p-4">
                  <label className="form-label mb-3 block">
                    Select Events 
                    {formData.applicable_to === "events" && (
                      <span className="text-[#a1a1aa] text-xs ml-2">(Leave empty for all events)</span>
                    )}
                  </label>
                  <div className="grid gap-2 max-h-40 overflow-y-auto">
                    {events.map(event => (
                      <label 
                        key={event.id} 
                        className="flex items-center gap-3 p-2 hover:bg-[rgba(212,175,55,0.05)] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.event_ids.includes(event.id)}
                          onChange={() => toggleEventSelection(event.id)}
                          className="w-4 h-4 accent-[#D4AF37]"
                        />
                        <span className="text-[#F5F5F0]">{event.title}</span>
                        <span className="text-[#a1a1aa] text-xs ml-auto">{event.date}</span>
                      </label>
                    ))}
                    {events.length === 0 && (
                      <p className="text-[#a1a1aa] text-sm">No events available</p>
                    )}
                  </div>
                </div>
              )}

              {/* Ticket Types Selection - Shows when events are selected */}
              {formData.event_ids.length > 0 && availableTicketTypes.length > 0 && (
                <div className="border border-[rgba(212,175,55,0.15)] p-4 bg-[rgba(212,175,55,0.02)]">
                  <label className="form-label mb-3 block">
                    Select Ticket Types
                    <span className="text-[#a1a1aa] text-xs ml-2">(Leave empty for all ticket types)</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableTicketTypes.map(ticketName => (
                      <label 
                        key={ticketName} 
                        className="flex items-center gap-3 p-2 hover:bg-[rgba(212,175,55,0.05)] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.ticket_types.includes(ticketName)}
                          onChange={() => toggleTicketType(ticketName)}
                          className="w-4 h-4 accent-[#D4AF37]"
                        />
                        <span className="text-[#F5F5F0] text-sm">{ticketName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Packages Selection */}
              {(formData.applicable_to === "experiences" || formData.applicable_to === "specific") && (
                <div className="border border-[rgba(212,175,55,0.15)] p-4">
                  <label className="form-label mb-3 block">
                    Select Experience Packages
                    {formData.applicable_to === "experiences" && (
                      <span className="text-[#a1a1aa] text-xs ml-2">(Leave empty for all packages)</span>
                    )}
                  </label>
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {packages.map(pkg => (
                      <label 
                        key={pkg.id} 
                        className="flex items-center gap-3 p-2 hover:bg-[rgba(212,175,55,0.05)] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.package_ids.includes(pkg.id)}
                          onChange={() => togglePackageSelection(pkg.id)}
                          className="w-4 h-4 accent-[#D4AF37]"
                        />
                        <span className="text-[#F5F5F0]">{pkg.name}</span>
                        <span className="text-[#D4AF37] text-sm ml-auto">{pkg.price_display}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Landing Pages Selection */}
              {showLandingPageSelection && (
                <div className="border border-[rgba(212,175,55,0.15)] p-4">
                  <label className="form-label mb-3 block">
                    Select Landing Pages
                    <span className="text-[#a1a1aa] text-xs ml-2">(Leave empty for all landing pages)</span>
                  </label>
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {landingPages.map(page => (
                      <label 
                        key={page.id} 
                        className="flex items-center gap-3 p-2 hover:bg-[rgba(212,175,55,0.05)] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.landing_page_ids.includes(page.id)}
                          onChange={() => toggleLandingPageSelection(page.id)}
                          className="w-4 h-4 accent-[#D4AF37]"
                        />
                        <span className="text-[#F5F5F0]">{page.title || page.slug}</span>
                        <span className="text-[#a1a1aa] text-xs ml-auto">/{page.slug}</span>
                      </label>
                    ))}
                    {landingPages.length === 0 && (
                      <p className="text-[#a1a1aa] text-sm">No landing pages available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="form-label">Max Uses (leave empty for unlimited)</label>
            <input
              type="number"
              value={formData.max_uses}
              onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              min="1"
              placeholder="Unlimited"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="form-label">Valid From</label>
              <input
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="form-label">Valid Until</label>
              <input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>

          {/* Active Status Toggle - Show when editing */}
          {isEditing && (
            <div className="flex items-center justify-between p-4 bg-[#121212] border border-[rgba(212,175,55,0.15)]">
              <div>
                <label className="form-label mb-0">Coupon Status</label>
                <p className="text-xs text-[#A1A1AA]">Toggle to activate or deactivate this coupon</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  formData.is_active ? 'bg-green-600' : 'bg-[#333]'
                }`}
                data-testid="coupon-active-toggle"
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    formData.is_active ? 'left-8' : 'left-1'
                  }`}
                />
              </button>
              <span className={`ml-3 text-sm font-medium ${formData.is_active ? 'text-green-500' : 'text-[#A1A1AA]'}`}>
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          )}

          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="coupon-submit-btn"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? "Update Coupon" : "Create Coupon"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminCouponsPage;
